import { action, observable, reaction, makeObservable } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react';
import React, { useEffect } from 'react';
import {
  authenticationLoaders,
  LoginSuccessResponse,
  SignInModel,
  userStatusService
} from '../../domain';
import { PromiseMessage } from '../../lib/components';
import { Disposers, HttpPromiseState, StringControl, validators } from '../../lib/helpers';
import './style.scss';
import { BlobComponent } from './BlobComponent';
import { User } from 'lib/components/icons/User';
import { Eye } from 'lib/components/icons/Eye';

class LoginSceneStore {
  usernameOrEmail: StringControl;
  password: StringControl;

  @observable.ref
  _signIn: HttpPromiseState<LoginSuccessResponse> | null;

  readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.usernameOrEmail = new StringControl(
      'usernameOrEmail', '', validators.required
    );
    this.usernameOrEmail.addValueChangeListener(this.clearSignInError);
    this.password = new StringControl(
      'password', '', validators.required
    );
    this.password.addValueChangeListener(this.clearSignInError);
    this._signIn = null;
    this.setup();
  }

  setup() {
    this.disposers.add(
      reaction(
        () => this._signIn?.state,
        this.afterSignIn));
  }

  signIn = () => {
    const { usernameOrEmail, password } = this;
    if (usernameOrEmail.invalid || password.invalid)
      return;
    if (this._signIn?.isPending)
      return;
    const model: SignInModel = {
      UsernameOrEmail: usernameOrEmail.value,
      Password: password.value
    };
    this._signIn = authenticationLoaders.signIn(model);
  };

  @action
  afterSignIn = () => {
    let state = this._signIn;
    if (state?.isResolved)
      userStatusService.refreshWhoAmI();
  };

  @action
  clearSignInError = () => {
    if (this._signIn?.isRejected)
      this._signIn = null;
  };

  dispose() {
    this.disposers.dispose();
  }
}

function LoginSceneComponent() {
  const store = useLocalObservable(() => new LoginSceneStore());
  const [passwordState, setPasswordState] = React.useState('password');

  function changePasswordState() {
    setPasswordState(passwordState === 'password' ? 'text' : 'password');
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter')
      store.signIn();
  };

  let { usernameOrEmail, password } = store;
  let hasError = usernameOrEmail.invalid || password.invalid;

  function scrollTo(id: string) {
    return () => {
      let element = document.getElementById(id);
      if (element)
        element.scrollIntoView({ behavior: 'smooth' });
    };
  }

  // REMOVE AFTER NEW PLATFORM DESIGN
  function attachStyles() {
    document.body.classList.add('login-page-layout');
    document.getElementsByClassName('main-container')[0].classList.add('login-page-layout');
    document.getElementsByClassName('app-layout-root')[0].classList.add('login-page-layout');
    document.getElementsByClassName('main-wrapper')[0].classList.add('login-page-layout');
  }

  // REMOVE AFTER NEW PLATFORM DESIGN
  function detachStyles() {
    document.body.classList.remove('login-page-layout');
    document.getElementsByClassName('main-container')[0].classList.remove('login-page-layout');
    document.getElementsByClassName('app-layout-root')[0].classList.remove('login-page-layout');
    document.getElementsByClassName('main-wrapper')[0].classList.remove('login-page-layout');
  }

  useEffect(() => {
    attachStyles();

    return () => {
      store.dispose();
      detachStyles();
    }
  }, [])

  const status = userStatusService.status;
  let pending = store._signIn?.isPending === true;
  return (
    <div className="login-page">
      {status.isUnknown &&
        <div className="login-alert alert-info">
          <p>Checking user session</p>
        </div>}

      {status.isServerUnreachable &&
        <div className="login-alert alert-danger">
          <p>Error connecting to server.</p>
          <p>Unable to establish session.</p>
        </div>}
      <BlobComponent />
      <PromiseMessage key={"sign-in-loader"} className={"login-alert"} label="Вход в систему:" state={store._signIn} align="nowrap" />
      {status.isLoggedOut &&
        <div className="login-content">
          <div className="header-menu">
            <a className='login-logo' href="https://site.tilzone.com"></a>
          </div>
          <div className='login-main'>
            <div className='login-form'>
              <h1>Добро пожаловать на платформу <span className='green-text'>tilzone!</span></h1>
              <div className='login-hint'>Если вы уже зарегистрированы, введите данные внизу</div>
              <form>
                <div className="login-group">
                  <label htmlFor='login'>Логин</label>
                  <div className='login-input'>
                    <input id="login" type="text" maxLength={60} onFocus={scrollTo('login')} value={usernameOrEmail.value} className="control-input"
                      disabled={pending}
                      onChange={usernameOrEmail.onChangeHandler} onKeyDown={onKeyDown} />
                    <User />
                  </div>
                </div>

                <div className="login-group password">
                  <label htmlFor='password'>Пароль</label>
                  <div className='login-input'>
                    <input id="password"
                      type={passwordState}
                      onFocus={scrollTo('password')}
                      maxLength={60}
                      value={password.value}
                      className="control-input"
                      disabled={pending}
                      onChange={password.onChangeHandler} onKeyDown={onKeyDown} />
                    <Eye className="cursor-pointer" onClick={changePasswordState} />
                  </div>
                </div>

                <div className="login-bottom">
                  <div className="login-remember">
                    <p className="rememberme">
                      <input type="checkbox" id="rememberID" />
                      <label htmlFor="rememberID"
                        className="remember-toggle"><span></span></label>
                      <label htmlFor="rememberID" className="remember-label">Запомнить меня</label>
                    </p>
                    <p className="forget-link"><a href="#">Забыли пароль?</a></p>
                  </div>
                  <div className="login-buttons">
                    <button type="button" className="login-btn"
                      onClick={store.signIn} disabled={pending || hasError}>Войти
                    </button>
                    <a type="button" className="sign-up-btn" href="https://site.tilzone.com/#course-form">Зарегистрироваться
                    </a>
                  </div>
                </div>
              </form>
            </div>
            <div className='image-circle'></div>
          </div>
          <div className='login-footer'>
            <div className='company-info'>Powered by Tilzone | © All Rights Reserved 2024.</div>
            <div className='links'>
              <a target="_blank" href="https://site.tilzone.com/offer">Договор - оферта</a>
              <a target="_blank" href="https://site.tilzone.com/policy">Политика конфиденциальности</a></div>
          </div>
        </div>
      }
    </div>
  );
}

export const LoginScene = observer(LoginSceneComponent);